import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipRoot = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

// Visual roxo do shell (decisão F80): mesmo spec do tooltip manual da Sidebar —
// fundo var(--theme-sidebar-active), texto branco, padding 6px 11px, 13px/500,
// radius 7px, seta na mesma cor. Técnica Radix mantida (delay, animação, portal).
// Sem overflow-hidden: o Arrow é posicionado fora do box do content e seria clipado.
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 8, children, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-[100]',
        'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      style={{
        backgroundColor: 'var(--theme-sidebar-active)',
        color: '#ffffff',
        padding: '6px 11px',
        fontSize: 13,
        fontWeight: 500,
        borderRadius: 7,
      }}
      data-testid="tooltip-content"
      {...props}
    >
      {children}
      {/* Seta na cor do corpo — proporção do triângulo CSS da Sidebar (12×6) */}
      <TooltipPrimitive.Arrow
        width={12}
        height={6}
        style={{ fill: 'var(--theme-sidebar-active)' }}
      />
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// ============================================================================
// Wrapper de conveniência
// ============================================================================

export interface TooltipProps {
  /** Conteúdo exibido no tooltip */
  content: React.ReactNode;
  /** Elemento que dispara o tooltip (recebe as props do trigger via asChild) */
  children: React.ReactNode;
  /** Lado do trigger em que o tooltip abre */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Alinhamento em relação ao trigger */
  align?: 'start' | 'center' | 'end';
  /** Delay antes de exibir, em ms */
  delayDuration?: number;
  /** Distância do trigger, em px */
  sideOffset?: number;
  /** className adicional para o content */
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration = 200,
  sideOffset,
  className,
}) => {
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipRoot>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent side={side} align={align} sideOffset={sideOffset} className={className}>
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
};

Tooltip.displayName = 'Tooltip';

export {
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
};
